import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import CountUpNumber from './CountUpNumber';
import HintLadderDemo from './HintLadderDemo';
import { isPromoActive, PROMO_END_LABEL } from '@/lib/promo';

const floatingBadges = [
];

// Served from /public so it can be preloaded from index.html (it is the LCP element).
const educationPattern = '/education-pattern.webp';

const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
const duration = 0.8;
const stagger = 0.1;

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger, delayChildren: 0.1 }
  }
};

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: duration * 0.6, ease: appleEase } }
};

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration, ease: appleEase } }
};

const lineReveal = {
  hidden: { scaleX: 0, originX: 0 },
  visible: { scaleX: 1, transition: { duration: duration * 0.7, ease: appleEase } }
};

// No blur in the hidden state — on slow connections a blurred headline reads
// as broken while fonts/JS settle. Fade + rise only.
const wordVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: appleEase } }
};

const imageReveal = {
  hidden: { opacity: 0, scale: 0.96, y: 20 },
  visible: {
    opacity: 1, scale: 1, y: 0,
    transition: { duration: duration * 1.2, ease: appleEase, delay: 0.3 }
  }
};

const stats = [
  { value: '50+', label: 'TCE Subjects' },
  { value: 'AI-Powered', label: 'Study Buddy' },
  { value: 'Tasmanian', label: 'Built for TCE Students' },
];

// Ambient orb configs
const orbs = [
  {
    className: 'w-[600px] h-[600px] -top-32 -left-48',
    style: { background: 'radial-gradient(circle, hsl(43 96% 49% / 0.10) 0%, transparent 70%)' },
    animate: { x: [0, 30, 0], y: [0, -20, 0] },
    duration: 12,
  },
  {
    className: 'w-[500px] h-[500px] top-1/2 -right-40',
    style: { background: 'radial-gradient(circle, hsl(214 70% 40% / 0.14) 0%, transparent 70%)' },
    animate: { x: [0, -25, 0], y: [0, 30, 0] },
    duration: 16,
  },
  {
    className: 'w-[400px] h-[400px] bottom-0 left-1/3',
    style: { background: 'radial-gradient(circle, hsl(43 96% 49% / 0.07) 0%, transparent 70%)' },
    animate: { x: [0, 20, 0], y: [0, -15, 0] },
    duration: 10,
  },
];

export default function HeroSection() {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const isInView = useInView(ref, { once: false, margin: '-50px' });

  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end start'] });
  const mascotY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const mascotOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.6]);

  const headlineWords = ['What', 'could', 'you'];

  return (
    <section ref={containerRef} className="relative min-h-[90vh] flex items-center bg-background overflow-hidden">
      {/* Education pattern */}
      <motion.div
        className="absolute inset-0 opacity-[0.10] pointer-events-none"
        initial={{ scale: 1.05 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2, ease: appleEase }}
        style={{
          backgroundImage: `url(${educationPattern})`,
          backgroundSize: '800px',
          backgroundRepeat: 'repeat'
        }}
      />

      {/* Ambient floating orbs */}
      {orbs.map((orb, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full pointer-events-none blur-3xl ${orb.className}`}
          style={orb.style}
          animate={orb.animate}
          transition={{ duration: orb.duration, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Premium gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/3 via-transparent to-transparent pointer-events-none" />

      <div ref={ref} className="container mx-auto px-6 md:px-8 lg:px-12 py-6 md:py-8 lg:py-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="max-w-2xl"
          >
            {isPromoActive() && (
              <motion.div variants={fadeIn} className="mb-8">
                <Link
                  to="/role-select"
                  className="group inline-flex items-baseline gap-4 text-foreground/80 hover:text-foreground transition-colors"
                >
                  <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-accent">Launch offer</span>
                  <span className="h-px w-8 self-center bg-accent/60 transition-all duration-500 group-hover:w-12" />
                  <span className="font-script italic text-base md:text-lg">
                    Free until {PROMO_END_LABEL}
                  </span>
                </Link>
              </motion.div>
            )}
            {/* Eyebrow */}
            <motion.p variants={fadeIn} className="text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-accent mb-6">
              Tasmania's AI-Powered Learning
            </motion.p>

            {/* Word-by-word staggered headline */}
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.02] mb-4">
              <span className="sr-only">Tasmania's AI-powered TASC tutoring — what could you unlock?</span>
              <motion.span
                aria-hidden="true"
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } } }}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                className="inline"
              >
                {headlineWords.map((word) => (
                  <motion.span
                    key={word}
                    variants={wordVariants}
                    className="inline-block mr-[0.25em]"
                  >
                    {word}
                  </motion.span>
                ))}
                <motion.span
                  variants={wordVariants}
                  className="inline-block font-script italic text-accent"
                >
                  unlock?
                </motion.span>
              </motion.span>
            </h1>

            {/* Divider */}
            <motion.div variants={lineReveal} className="h-[2px] w-20 bg-gradient-to-r from-foreground/30 to-transparent mb-8" />

            {/* Description */}
            <motion.div variants={slideUp} className="relative mb-10">
              <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-[1.6]">
                <span className="text-accent font-semibold">UnlockEd Tutoring</span> is your personal AI tutor aligned to TASC courses—here to{' '}
                <span className="text-accent font-semibold">Unlock</span>{' '}
                your{' '}
                <span className="text-accent font-semibold">Ed</span>ucation, track your growth, and help you achieve your goals at your own pace.
              </p>
              <p className="mt-4 text-base md:text-lg text-muted-foreground/80 leading-[1.6]">
                Whether you're in Hobart, Launceston, Devonport, Burnie or somewhere much smaller, UnlockEd works wherever you've got a connection.
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={slideUp} className="flex flex-col sm:flex-row gap-4 relative z-10 mb-10">
              <Button asChild size="lg" variant="accent" className="group w-full sm:w-auto text-base px-10 py-6 rounded-full cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-accent/25 hover:scale-[1.02] active:scale-[0.98]">
                <Link to="/role-select">Get Started Free</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="group w-full sm:w-auto text-base px-8 py-6 rounded-full cursor-pointer transition-all duration-300 hover:bg-foreground/5 hover:scale-[1.02] active:scale-[0.98]">
                <Link to="/auth">Sign In</Link>
              </Button>
            </motion.div>

            {/* Stats trust-bar */}
            <motion.div
              variants={fadeIn}
              className="flex items-center gap-0 flex-wrap"
            >
              {stats.map((stat, i) => (
                <div key={stat.label} className="flex items-center gap-0">
                  <div className="flex flex-col items-start px-4 first:pl-0">
                    <CountUpNumber value={stat.value} className="text-sm font-bold text-accent leading-tight" />
                    <span className="text-xs text-muted-foreground">{stat.label}</span>
                  </div>
                  {i < stats.length - 1 && (
                    <div className="w-px h-8 bg-border mx-1 shrink-0" />
                  )}
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Hero Visual with parallax */}
          <motion.div
            variants={imageReveal}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="relative hidden lg:flex lg:flex-col lg:items-center lg:justify-center"
          >
            <motion.div
              style={{ y: mascotY, opacity: mascotOpacity }}
              className="relative flex flex-col items-center w-full max-w-[500px] mt-12"
            >
              {/* Soft glow behind the product preview */}
              <div
                className="absolute inset-0 -z-10 blur-[60px] opacity-20 rounded-full"
                style={{ background: 'radial-gradient(circle, hsl(43 96% 49% / 0.4) 0%, transparent 65%)' }}
              />
              {floatingBadges.map((b, i) => (
                <motion.div
                  key={b.text}
                  className={`absolute ${b.pos} rounded-full border border-border/50 bg-card/80 backdrop-blur-xl px-3 py-1.5 text-xs font-semibold shadow-lg whitespace-nowrap`}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut' }}
                >
                  {b.text}
                </motion.div>
              ))}
              <HintLadderDemo className="w-full relative" />

            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
